import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, UserProfile, AuthState } from '../types';
import { authAPI } from '../services/api';
import api from '../services/api';

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  register: (data: { email: string; password: string; firstName: string; lastName: string; companyName?: string; industrySlug?: string }) => Promise<void>;
  logout: () => void;
  updateProfile: (profile: UserProfile) => void;
  refreshUser: () => Promise<void>;
  impersonate: (userId: string) => Promise<void>;
  stopImpersonating: () => Promise<void>;
  isImpersonating: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    profile: null,
    token: localStorage.getItem('token'),
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await authAPI.getMe();
          setState({
            user: response.data.user,
            profile: response.data.user.profile,
            token,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          localStorage.removeItem('token');
          setState({
            user: null,
            profile: null,
            token: null,
            isLoading: false,
            error: null,
          });
        }
      } else {
        setState(prev => ({ ...prev, isLoading: false }));
      }
    };

    loadUser();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      const response = await authAPI.login({ email, password });
      const { user, token } = response.data;
      
      localStorage.setItem('token', token);
      // Fetch full user with organization/industry included
      const meResponse = await authAPI.getMe();
      const fullUser = meResponse.data.user;
      setState({
        user: fullUser,
        profile: fullUser.profile,
        token,
        isLoading: false,
        error: null,
      });
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error.response?.data?.error || 'Login failed',
      }));
      throw error;
    }
  };

  const register = async (data: { email: string; password: string; firstName: string; lastName: string; companyName?: string; industrySlug?: string }) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      const response = await authAPI.register(data);
      const { user, profile, token } = response.data;
      
      localStorage.setItem('token', token);
      setState({
        user,
        profile,
        token,
        isLoading: false,
        error: null,
      });
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error.response?.data?.error || error.response?.data?.errors?.[0]?.msg || 'Registration failed',
      }));
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setState({
      user: null,
      profile: null,
      token: null,
      isLoading: false,
      error: null,
    });
  };

  const updateProfile = (profile: UserProfile) => {
    setState(prev => ({
      ...prev,
      profile,
      user: prev.user ? {
        ...prev.user,
        profile
      } : null
    }));
  };

  const isImpersonating = !!localStorage.getItem('adminToken');

  const impersonate = async (userId: string) => {
    try {
      const currentToken = localStorage.getItem('token');
      const response = await api.post(`/user-management/${userId}/impersonate`);
      const { token } = response.data;

      // Save admin token so we can switch back
      localStorage.setItem('adminToken', currentToken || '');
      localStorage.setItem('token', token);

      // Reload user state
      const meResponse = await authAPI.getMe();
      const fullUser = meResponse.data.user;
      setState({
        user: fullUser,
        profile: fullUser.profile,
        token,
        isLoading: false,
        error: null,
      });
    } catch (error: any) {
      throw error;
    }
  };

  const stopImpersonating = async () => {
    const adminToken = localStorage.getItem('adminToken');
    if (!adminToken) return;

    localStorage.setItem('token', adminToken);
    localStorage.removeItem('adminToken');

    // Reload admin user state
    const meResponse = await authAPI.getMe();
    const fullUser = meResponse.data.user;
    setState({
      user: fullUser,
      profile: fullUser.profile,
      token: adminToken,
      isLoading: false,
      error: null,
    });
  };

  const refreshUser = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await authAPI.getMe();
        setState(prev => ({
          ...prev,
          user: response.data.user,
          profile: response.data.user.profile,
        }));
      } catch (error) {
        console.error('Failed to refresh user:', error);
      }
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        register,
        logout,
        updateProfile,
        refreshUser,
        impersonate,
        stopImpersonating,
        isImpersonating,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};